import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Which of the two side panels — the main menu and the context rail — are open.
 *
 * Three regimes, widest to narrowest:
 *
 * - Roomy: both panels are independent and the choice is remembered.
 * - Compact: not enough width for both alongside the inbox and the thread, so
 *   at most one may be open — opening either closes the other. That rule is
 *   expressed by the shape of the state rather than enforced by checks: compact
 *   mode has a single slot, so an invalid pair cannot be represented.
 * - Mobile: the same single slot, but the panel that fills it floats *over* the
 *   content as a drawer rather than taking a column beside it, and the slot
 *   starts empty. A phone has room for exactly one thing at a time, and that
 *   thing is the list or the thread the agent came here to read.
 *
 * 1600px rather than the 1500px the rail used to hide at: a 14" laptop reports
 * 1512 logical pixels, and that machine is exactly the case this exists for.
 * 860px is where the three-pane layout stops being a layout at all — below it
 * the inbox and the thread each need the whole screen.
 */
const COMPACT_QUERY = '(max-width: 1600px)';
const MOBILE_QUERY = '(max-width: 860px)';
const KEY = 'superchat:panels';

const PanelsContext = createContext(null);

function readPreference() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    // Default open, so an agent who has never touched this sees no change.
    return { nav: raw.nav !== false, rail: raw.rail !== false };
  } catch {
    return { nav: true, rail: true };
  }
}

export function PanelsProvider({ children }) {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  /* Wide-screen layout, persisted. Compact mode never writes here, so shrinking
     the window and growing it back restores what the agent actually chose. */
  const [preference, setPreference] = useState(readPreference);

  /* The one panel open while compact — nothing at all on a phone, where a
     drawer over the content is something the agent asks for, not a greeting. */
  const [slot, setSlot] = useState(() =>
    window.matchMedia(MOBILE_QUERY).matches
      ? null
      : window.matchMedia(COMPACT_QUERY).matches
        ? 'nav'
        : null
  );

  useEffect(() => {
    const compactMq = window.matchMedia(COMPACT_QUERY);
    const mobileMq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => {
      const isCompact = compactMq.matches;
      const isMobile = mobileMq.matches;
      setCompact(isCompact);
      setMobile(isMobile);
      // Crossing a breakpoint starts from the menu rather than whatever was
      // open before, which may no longer fit — and from nothing on a phone,
      // where the menu covers the screen it was opened from.
      setSlot(isMobile ? null : isCompact ? 'nav' : null);
    };
    compactMq.addEventListener('change', onChange);
    mobileMq.addEventListener('change', onChange);
    return () => {
      compactMq.removeEventListener('change', onChange);
      mobileMq.removeEventListener('change', onChange);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(preference));
    } catch {
      /* Private mode: the layout still works, it just won't be remembered. */
    }
  }, [preference]);

  const toggle = useCallback(
    (panel) => {
      if (compact) setSlot((current) => (current === panel ? null : panel));
      else setPreference((current) => ({ ...current, [panel]: !current[panel] }));
    },
    [compact]
  );

  const close = useCallback(
    (panel) => {
      if (compact) setSlot((current) => (current === panel ? null : current));
      else setPreference((current) => ({ ...current, [panel]: false }));
    },
    [compact]
  );

  const value = useMemo(
    () => ({
      compact,
      mobile,
      nav: compact ? slot === 'nav' : preference.nav,
      rail: compact ? slot === 'rail' : preference.rail,
      toggle,
      close,
    }),
    [compact, mobile, slot, preference, toggle, close]
  );

  return <PanelsContext.Provider value={value}>{children}</PanelsContext.Provider>;
}

export function usePanels() {
  const value = useContext(PanelsContext);
  if (!value) throw new Error('usePanels must be used inside PanelsProvider');
  return value;
}

export default PanelsProvider;
